import type { InboxFilter } from "./hooks";
import type { InboxSegment } from "../../api/inbox";

/** Inbox copy. English only. */
export const inbox = {
  title: "Inbox",
  eyebrow: (branch: string) => `WhatsApp${branch ? ` · ${branch}` : ""}`,
  segments: { lead: "Leads", member: "Members", staff: "Staff" } satisfies Record<InboxSegment, string>,
  segLabel: "Chat type",
  segUnread: (label: string, n: number) => `${label}, ${n} unread`,
  search: { name: "Search by name", namePhone: "Search by name or phone", label: "Search chats", phoneNeedsAccess: "Phone search is off for your role. Search by name." },
  number: { all: "All numbers", label: (current: string) => `WhatsApp number: ${current}. Change`, title: "WhatsApp number", sub: "Show chats from one number or all of them", allSub: "Every chat on every number" },
  filters: { all: "All", unread: "Unread", mine: "Mine", unassigned: "Unassigned" } satisfies Record<InboxFilter, string>,
  filterLabel: "Filter chats",
  newContact: "New contact",
  archivedLink: "Archived chats",
  activeLink: "Back to chats",
  loadMore: "Load more",
  loading: "Loading chats",
  error: "Could not load chats",
  notConnected: "the Inbox",
  empty: {
    base: (seg: InboxSegment) => ({ lead: "No lead chats yet", member: "No member chats yet", staff: "No staff chats yet" })[seg],
    baseBody: "Chats appear here when someone messages your WhatsApp number, or when you message them.",
    unread: (seg: InboxSegment) => `No unread chats from ${({ lead: "leads", member: "members", staff: "staff" })[seg]}`,
    mine: "No chats are assigned to you", unassigned: "Every chat has an owner", archived: "Nothing archived", search: (q: string) => `No chats match “${q}”`,
    filterBody: "Try another filter or number.",
  },
  swipe: { hint: "Swipe right for read or unread, left to archive", read: "Read", unread: "Unread", archive: "Archive", unarchive: "Unarchive" },
  window: { open: (label: string) => label, closed: "Template only" },
  assignee: { none: "Unassigned", you: "You" },
  status: { pending: "Sending", sent: "Sent", delivered: "Delivered", read: "Read", failed: "Not sent" },
  thread: {
    loadEarlier: "Load earlier messages",
    back: "Back to chats", info: (name: string) => `Contact info for ${name}`, assign: (who: string) => `Assigned to ${who}. Change`, loading: "Loading messages", error: "Could not load this chat",
    empty: "No messages yet", emptyBody: "Send a template to start the conversation.", notFound: "That chat does not exist", select: "Select a chat", selectBody: "Pick a conversation from the list.",
    today: "Today", yesterday: "Yesterday", sentBy: (n: string) => `Sent by ${n}`, template: "Template", media: "Media", mediaNote: "Photo or document (not shown yet)", buttonInert: "Button (not active)",
    typeLabel: { lead: "Lead", member: "Member", staff: "Staff", unknown: "New contact" } as const,
  },
  composer: {
    placeholder: "Message", closedPlaceholder: "Free text is off until they reply", send: "Send", sendTemplate: "Send template", template: "Template",
    closedBanner: (name: string, date: string | null) => (date ? `24-hour window closed. ${name} last wrote on ${date}, so only an approved template can go out.` : `24-hour window closed. ${name} has not written yet, so only an approved template can go out.`),
    sentBanner: (name: string) => `Template sent. Free text opens when ${name} replies.`,
    quickLabel: "Quick replies", quickDisabled: "Quick replies", sendFailed: "Could not send. Your message is still in the box.", retry: "Try again",
    quick: [
      { label: "Checking", text: "Hi {f}, thanks for writing in! Give me a minute to check." },
      { label: "Link coming", text: "Sending you a payment link now." },
      { label: "Booked", text: "Sure, it is booked. See you at the gym!" },
    ],
    closedFlip: "The 24-hour window just closed. Your message is kept; send a template instead.",
  },
  templates: {
    title: "Send a template", sub: "Only approved templates can go out after 24 hours", preview: "Preview", empty: "No approved templates", emptyBody: "Templates need approval before staff can send them.",
    error: "Could not load templates", send: "Send", sending: "Sending…", sent: "Template sent", failed: "Could not send that template. Try again.", fill: (p: string) => p.replace(/_/g, " "),
    category: (c: string) => c,
  },
  info: {
    title: "Contact", type: "Type", assigned: "Assigned to", plan: "Plan or stage", lastWrote: "Last wrote", never: "Never",
    call: "Call", openMember: "Open member", openLead: "Open lead", collect: "Collect payment", archive: "Archive chat", unarchive: "Unarchive chat", markUnread: "Mark unread", phone: "Phone",
    archived: "Chat archived", unarchived: "Chat moved back to your inbox", updateFailed: "Could not update that chat.",
  },
  assign: { title: "Assign chat", sub: "Who looks after this conversation?", unassign: "Unassigned", failed: "Could not assign that chat.", done: (n: string) => `Assigned to ${n}`, error: "Could not load staff" },
} as const;
